import { useState, useEffect, useRef } from "react";
import dropdownIcon from "../../assets/Icons/Dropdown.svg";
import {Icon} from '@iconify/react';

const normalizeOptionText = (value) => `${value || ""}`.trim().toLowerCase();

const getOptionSearchText = (option, valueKey) => [
  option?.[valueKey],
  ...(option?.searchAliases || []),
].map(normalizeOptionText).join(" ");

function CustomDropDown({
  label,
  id,
  name,
  value,
  onChange,
  onBlur,
  error,
  options,
  defaultOption = "Select an option",
  idKey,
  valueKey,
  dropdownDivStyle = "",
  labelStyle = "",
  dropdownStyle = "",
  dropDownIconStyle = "",
  optionStyle = "",
  disabled = false,
  extraAdderKey = "",
  displayKey = "",
  enableHoverShadow = true,
  tooltip,
  searchable = false,
  searchPlaceholder = "Type to search...",
  fallbackSearchValue = "",
}) {

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const otherSubjectOption = (options || []).find(
    (option) => normalizeOptionText(option?.[valueKey]) === "other subject"
  );
  const fallbackSearchOption = fallbackSearchValue
    ? (options || []).find((option) => normalizeOptionText(option?.[valueKey]) === normalizeOptionText(fallbackSearchValue))
    : null;

  const filteredOptions = searchable && searchQuery.trim()
    ? (options || []).filter((option) =>
        getOptionSearchText(option, valueKey).includes(normalizeOptionText(searchQuery))
      )
    : (options || []);

  const visibleOptions = searchable && searchQuery.trim() && filteredOptions.length < 1
    ? [fallbackSearchOption || otherSubjectOption].filter(Boolean)
    : filteredOptions;
  const selectedOption = (options || []).find((option) => option?.[valueKey] === value);
  const selectedDisplayValue = selectedOption?.[displayKey] || value;

  const handleSelect = (option) => {
    onChange(name, option[valueKey]);
    onBlur(name, option[valueKey]);
    setSearchQuery("");
    setIsOpen(false);
  };

  const handleOpen = () => {
    if (disabled) return;
    setIsOpen(true);
    setSearchQuery("");
  };

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen((current) => !current);
    setSearchQuery("");
  };

  useEffect(() => {
    if (isOpen && searchable) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen, searchable]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="mb-6" ref={dropdownRef}>
      {label && (
        <label
          htmlFor={id}
          className={`form-label block font-inter text-secondary text-level-3 
            font-medium leading-[24px] ${labelStyle} flex items-center gap-1`
          }
        >
          {label} <span className="text-light-red">*</span>

          {
            tooltip && 
            <div className="relative group cursor-pointer">
              <Icon
                icon="mdi:help-circle-outline"
                className="text-[#6B7280]"
                width={18}
                height={18}
              />
              <div className="absolute z-20 hidden group-hover:block bg-black text-white text-sm 
              rounded-md px-2 py-1 top-full mt-1 left-1/2 -translate-x-1/2 
              min-w-[300px] break-words text-center whitespace-normal">
                {tooltip}
              </div>
            </div>
          }
        </label>
      )}
      <div
        className={`relative mt-3 rounded-[16px] border transition-all duration-200 ${
          error
            ? "border-red-200 bg-red-50/40 shadow-[0_12px_30px_rgba(239,68,68,0.08)]"
          : isOpen
              ? "z-40 border-blue-200 bg-blue-50/60 shadow-[0_18px_40px_rgba(0,32,189,0.16)]"
              : `border-slate-200 bg-white shadow-[0_10px_24px_rgba(15,23,42,0.06)] ${
                  enableHoverShadow
                    ? "hover:border-blue-200 hover:bg-blue-50/40 hover:shadow-[0_16px_36px_rgba(0,32,189,0.12)]"
                    : ""
                }`
        } ${dropdownDivStyle}`}
      >
        <div
          className={`flex items-center justify-between px-4 font-inter py-3 h-[52px] w-full rounded-[16px] text-sm border border-transparent cursor-pointer transition-colors duration-200 ${
            isOpen ? "bg-blue-50/70" : "bg-transparent"
          } ${dropdownStyle} ${
            disabled ? "opacity-50 cursor-not-allowed" : ""
          } ${value ? "text-slate-900" : "text-slate-500"}`}
          aria-expanded={isOpen}
          role="button"
          tabIndex="0"
          onClick={searchable ? handleOpen : handleToggle}
        >
          {searchable && isOpen ? (
            <input
              ref={searchInputRef}
              id={id}
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onClick={(event) => event.stopPropagation()}
              placeholder={value || searchPlaceholder}
              className={`min-w-0 flex-1 bg-transparent text-sm text-slate-900 placeholder:text-slate-500 outline-none`}
            />
          ) : (
            <span className="min-w-0 truncate">{value ? selectedDisplayValue : defaultOption}</span>
          )}
          <img
            src={dropdownIcon}
            alt="dropdown"
            className={`w-4 h-4 ml-2 ${dropDownIconStyle}`}
          />
        </div>
        {error && (
          <p className="mt-2 text-xs font-medium text-red-400">
            {error}
          </p>
        )}
        {isOpen && (
          <ul
            className="absolute left-0 right-0 mt-2 border border-blue-100 bg-white/95 backdrop-blur-sm rounded-[16px] z-50"
            style={{
              boxShadow: "0px 22px 50px rgba(0, 32, 189, 0.14)",
              maxHeight: "250px",
              overflowY: "auto",
            }}
            role="menu"
          >
            {visibleOptions.map((option, index) => {
              const previousOption = visibleOptions[index - 1];
              const showCategory = option.category && option.category !== previousOption?.category;

              return (
                <li key={option[idKey] || `${option[valueKey]}-${index}`} className="list-none">
                  {showCategory && (
                    <div className="mx-2 mt-3 px-4 pb-1 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">
                      {option.category}
                    </div>
                  )}
                  <div
                    onClick={() => handleSelect(option)}
                    className={`mx-2 my-1 flex items-center justify-between px-4 py-3 rounded-[12px] cursor-pointer transition-all duration-150 ${optionStyle} ${
                      value === option[valueKey]
                        ? "bg-black text-white shadow-[0_10px_24px_rgba(0,0,0,0.18)]"
                        : "text-slate-700 hover:bg-blue-50 hover:text-blue-800 active:bg-blue-100"
                    }`}
                    role="menuitem"
                    tabIndex="0"
                  >
                    <span className="min-w-0 text-left">{(option[displayKey] || option[valueKey]) + (option[extraAdderKey] ? " " + option[extraAdderKey] : "")}</span>
                    {value === option[valueKey] && (
                      <Icon icon="mdi:check" className="ml-2 h-5 w-5 shrink-0 text-sky-300" />
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export default CustomDropDown;
